import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { FilterableDataGrid } from '../../components/common';
import { useNotifications } from '../../context/NotificationContext';
import { dsPackageApi } from '../../services/api';
import DsPackagePreviewModal from './DsPackagePreviewModal';

import { applyColumnFilters } from '../../utils/listSearch';
/**
 * Review panel for one package's datasheets.
 *
 * Both views render through ONE row shape and ONE columns array, so the by-tag and by-index
 * tables cannot drift apart in columns, order or width. What changes between them is only what
 * a row represents: a tag, or an index sheet. That follows the package's exec type, which is
 * fixed at generation, so there is nothing to toggle.
 *
 * Column widths are passed in with no onColumnResize handler, which makes them immovable —
 * FilterableDataGrid only applies a drag when that callback exists.
 */

const RECORD_STATUS = {
  D:  { label: 'Development', badge: 'stui-badge-gray' },
  R:  { label: 'In Review',   badge: 'stui-badge-amber' },
  DR: { label: 'Returned',    badge: 'stui-badge-orange' },
  A:  { label: 'Approved',    badge: 'stui-badge-green' },
};

const COLUMN_WIDTHS = {
  datasheet:      230,
  indexSheetCode: 180,
  tagCount:       80,
  dsStatus:       130,
  lateCount:      80,
};

const Badge = ({ value }) => {
  const meta = RECORD_STATUS[value];
  return (
    <span className={`stui-badge ${meta?.badge || 'stui-badge-gray'}`}>
      {meta?.label || value || '—'}
    </span>
  );
};

export default function DsPackageRecordsPanel({ pkg, auditCfg, onChanged, onExport }) {
  const { notify } = useNotifications();
  const [preview, setPreview] = useState(null);
  const [rows, setRows] = useState([]);
  const [groups, setGroups] = useState([]);
  const [filters, setFilters] = useState({});
  const [busy, setBusy] = useState(false);

  // "Tag based" = one datasheet per tag; "Index based" = one per index sheet.
  const byIndex = pkg?.dsExecType === 'Index based';

  const load = useCallback(async () => {
    if (!pkg) return;
    try {
      const res = await dsPackageApi.records(pkg.id, 'index');
      setRows(res.data.rows || []);
      setGroups(res.data.groups || []);
    } catch (err) {
      notify.error(err?.response?.data?.error || 'Failed to load package records.');
    }
  }, [pkg, notify]);

  useEffect(() => { load(); }, [load]);

  // One call for the whole set, all or nothing. Never a loop of single updates: a half-applied
  // group approval cannot be told apart from a completed one afterwards.
  const setStatus = useCallback(async (recordIds, status, what) => {
    if (!recordIds.length) return;
    setBusy(true);
    try {
      const res = await dsPackageApi.setRecordStatus(pkg.id, recordIds, status, auditCfg);
      notify.success(`${what}: ${res.data.updated} record${res.data.updated === 1 ? '' : 's'}`);
      await load();
      onChanged?.();
    } catch (err) {
      const data = err?.response?.data;
      if (data?.rejected?.length) {
        // The server refuses the whole batch and says why, per record. Surface the first few —
        // "not a legal transition" is actionable, "failed" is not.
        notify.error(`Nothing changed. ${data.rejected.slice(0, 3).join(' ')}`);
      } else {
        notify.error(data?.error || `${what} failed.`);
      }
    } finally {
      setBusy(false);
    }
  }, [pkg, auditCfg, notify, load, onChanged]);

  /**
   * ONE normalised row shape for both views. Every field means the same thing in each, so the
   * single columns array below renders identically whichever way the package was generated.
   *
   *   datasheet      what this datasheet IS — a tag, or an index sheet
   *   indexSheetCode which index sheet it belongs to (the template source)
   *   tagCount       how many tags the datasheet covers (always 1 when tag-based)
   *   dsStatus       status, rolled up to the weakest member when index-based
   *   lateCount      records that arrived after the initial generation
   *   previewId      the record whose frozen snapshot the preview renders
   *   memberIds      every record the row stands for - the preview's actions apply to all of them
   */
  const gridRows = useMemo(() => {
    if (byIndex) {
      return groups.map(g => ({
        id: g.indexSheetId ?? `none-${g.indexSheetCode}`,
        datasheet: g.indexSheetCode || '—',
        indexSheetCode: g.indexSheetCode || '—',
        tagCount: g.recordCount,
        dsStatus: g.rollupStatus,
        lateCount: g.lateAdditionCount,
        previewId: g.recordIds[0],
        memberIds: g.recordIds,
      }));
    }
    return rows.map(r => ({
      id: r.id,
      datasheet: r.tagId || `Record ${r.id}`,
      indexSheetCode: r.indexSheetCode || '—',
      tagCount: 1,
      dsStatus: r.dsStatus,
      lateCount: r.isLateAddition ? 1 : 0,
      previewId: r.id,
      memberIds: [r.id],
    }));
  }, [byIndex, groups, rows]);

  // Clicking a row opens its review. An index-based row is one datasheet covering several tag
  // records: the preview draws the first one but acts for the whole index sheet, so it carries
  // the row's members and its rolled-up status - otherwise Submit would move one tag and leave
  // the index sheet showing its old status. Return, Submit and Approve live in the preview footer.
  const openReview = useCallback((row) => {
    const rec = rows.find(r => r.id === row.previewId);
    if (rec) setPreview({ record: { ...rec, dsStatus: row.dsStatus }, memberIds: row.memberIds });
  }, [rows]);

  const columns = useMemo(() => [
    { key: 'datasheet', label: byIndex ? 'Index Sheet' : 'Tag' },
    { key: 'indexSheetCode', label: 'Index Sheet' },
    { key: 'tagCount', label: 'Tags' },
    { key: 'dsStatus', label: 'Status', renderCell: (row) => <Badge value={row.dsStatus} /> },
    {
      key: 'lateCount',
      label: 'Late',
      renderCell: (row) => (row.lateCount > 0
        ? <span className="stui-badge stui-badge-blue">{row.lateCount}</span>
        : '—'),
    },
  ], [byIndex]);

  if (!pkg) return null;

  return (
    <div>
      <div className="stui-detail-grid">
        <div className="stui-col-24">
          <div className="stui-detail-section-header">
            {byIndex
              ? `Datasheets by Index Sheet (${gridRows.length})`
              : `Datasheets by Tag (${gridRows.length})`}
          </div>
        </div>
      </div>

      <FilterableDataGrid
        data={applyColumnFilters(gridRows, filters)}
        allData={gridRows}
        columns={columns}
        filters={filters}
        setFilters={setFilters}
        columnWidths={COLUMN_WIDTHS}
        onRowClick={openReview}
      />

      {preview && (
        <DsPackagePreviewModal
          pkg={pkg}
          record={preview.record}
          busy={busy}
          onExport={onExport}
          onClose={() => setPreview(null)}
          onAction={async (_row, status) => {
            const what = status === 'A' ? 'Approved' : status === 'DR' ? 'Returned' : 'Submitted';
            // Submit moves the members still in Development; Approve and Return move the ones
            // In Review. Members already past that point are left alone.
            const from = status === 'R' ? 'D' : 'R';
            const ids = rows
              .filter(r => preview.memberIds.includes(r.id) && r.dsStatus === from)
              .map(r => r.id);
            await setStatus(ids, status, what);
            setPreview(null);
          }}
        />
      )}
    </div>
  );
}
